import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Clock, BookOpen, MapPin, ShieldCheck, User,
  CalendarClock, ClipboardCheck, SearchX, BrainCircuit, FileEdit, FileSearch,
  type LucideIcon,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { PublicHeader } from '../components/PublicHeader';
import { useForceLightMode } from '../hooks/useForceLightMode';
import { blogPosts } from '../data/blogPosts';

const categoryStyles: Record<string, { bg: string; text: string; dot: string }> = {
  blue:    { bg: 'bg-cyan-50',    text: 'text-cyan-700',    dot: 'bg-cyan-500' },
  green:   { bg: 'bg-green-50',   text: 'text-green-700',   dot: 'bg-green-500' },
  red:     { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500' },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  dot: 'bg-indigo-500' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
};

const heroIcons: Record<string, LucideIcon> = {
  'calendar-clock': CalendarClock,
  'clipboard-check': ClipboardCheck,
  'search-x': SearchX,
  'brain-circuit': BrainCircuit,
  'shield-check': ShieldCheck,
  'file-edit': FileEdit,
  'file-search': FileSearch,
};

const scroll = () => window.scrollTo(0, 0);

export const BlogListPage: React.FC = () => {
  useForceLightMode();

  const [featured, ...rest] = blogPosts;

  const featuredStyle = featured
    ? categoryStyles[featured.categoryColor] ?? categoryStyles.blue
    : categoryStyles.blue;

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50/30">
      <PublicHeader fixed />

      {/* Hero */}
      <section className="pt-36 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-medical-900 tracking-tight mb-4">
            Insights for medico-legal professionals
          </h1>
          <p className="text-lg text-medical-500 max-w-2xl mx-auto leading-relaxed">
            Practical guidance on AI-powered case preparation, clinical-guideline compliance,
            data security and report drafting in UK medico-legal practice.
          </p>
        </div>
      </section>

      {/* Featured post */}
      {featured && (
        <section className="px-4 sm:px-6 lg:px-8 pb-12">
          <div className="max-w-7xl mx-auto">
            <Link
              to={`/blog/${featured.slug}`}
              onClick={scroll}
              className="group relative grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-2xl border border-medical-200/60 overflow-hidden hover:shadow-xl hover:shadow-primary-100/20 transition-all duration-500"
            >
              {/* Hero — graphic (ogImage) or colour panel (icon fallback) */}
              {featured.ogImage ? (
                <div className="aspect-[2/1] self-start w-full overflow-hidden bg-medical-50">
                  <img
                    src={featured.ogImage}
                    alt={featured.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className={`relative flex items-center justify-center p-12 lg:p-16 ${featuredStyle.bg} overflow-hidden`}>
                  {/* Large background icon */}
                  {(() => {
                    const Icon = heroIcons[featured.heroIcon] ?? BookOpen;
                    return <Icon className={`absolute h-48 w-48 ${featuredStyle.text} opacity-[0.07]`} strokeWidth={1} />;
                  })()}
                  {/* Foreground */}
                  <div className="relative text-center space-y-5">
                    {(() => {
                      const Icon = heroIcons[featured.heroIcon] ?? BookOpen;
                      return (
                        <div className={`mx-auto w-16 h-16 rounded-2xl bg-white/80 shadow-sm flex items-center justify-center`}>
                          <Icon className={`h-8 w-8 ${featuredStyle.text}`} strokeWidth={1.5} />
                        </div>
                      );
                    })()}
                    <span className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/60 ${featuredStyle.text}`}>
                      <span className={`w-2 h-2 rounded-full ${featuredStyle.dot}`} />
                      <span>{featured.category}</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="px-8 pb-6 pt-5 lg:px-12 lg:pb-8 lg:pt-6 flex flex-col">
                <span className="text-xs font-semibold tracking-widest uppercase text-primary-600 mb-2">Featured</span>
                <h2 className="text-xl lg:text-2xl font-bold text-medical-900 mb-3 group-hover:text-primary-700 transition-colors leading-tight">
                  {featured.title}
                </h2>
                <p className="text-medical-500 leading-relaxed mb-4 line-clamp-3">
                  {featured.subtitle}
                </p>

                {/* Author + meta */}
                <div className="flex items-center justify-between pt-4 border-t border-medical-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-medical-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-medical-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-medical-800">{featured.author}</p>
                      <p className="text-xs text-medical-400">{featured.authorRole}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-medical-400">
                    <time>
                      {new Date(featured.publishedDate).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </time>
                    <span className="inline-flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{featured.readingTime}</span>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Post grid */}
      {rest.length > 0 && (
        <section className="pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((post) => {
              const style = categoryStyles[post.categoryColor] ?? categoryStyles.blue;
              return (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  onClick={scroll}
                  className="group relative bg-white rounded-2xl border border-medical-200/60 hover:border-primary-300/50 transition-all duration-500 hover:shadow-xl hover:shadow-primary-100/20 flex flex-col overflow-hidden"
                >
                  {/* Hero — OG graphic if present, otherwise colour strip */}
                  {post.ogImage ? (
                    <div className="aspect-[2/1] w-full overflow-hidden bg-medical-50">
                      <img
                        src={post.ogImage}
                        alt={post.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`h-1 w-full ${style.dot}`} />
                  )}

                  <div className="p-7 flex flex-col flex-1">
                    {/* Category + reading time */}
                    <div className="flex items-center justify-between mb-5">
                      <span
                        className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                        <span>{post.category}</span>
                      </span>
                      <span className="inline-flex items-center space-x-1 text-xs text-medical-400">
                        <Clock className="h-3 w-3" />
                        <span>{post.readingTime}</span>
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg font-semibold text-medical-900 mb-3 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug">
                      {post.title}
                    </h2>

                    {/* Subtitle */}
                    <p className="text-sm text-medical-500 leading-relaxed line-clamp-3 flex-1">
                      {post.subtitle}
                    </p>

                    {/* Divider + footer */}
                    <div className="mt-6 pt-5 border-t border-medical-100 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 rounded-full bg-medical-100 flex items-center justify-center">
                          <User className="h-3 w-3 text-medical-400" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-medical-700 leading-none">{post.author}</p>
                          <time className="text-[10px] text-medical-400">
                            {new Date(post.publishedDate).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </time>
                        </div>
                      </div>
                      <span className="inline-flex items-center text-sm font-medium text-primary-600 group-hover:text-primary-700 transition-colors">
                        Read
                        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Footer — matches landing page */}
      <footer className="bg-medical-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <Logo size="md" className="mb-4" variant="dark" />
              <p className="text-medical-300 mb-6 max-w-md leading-relaxed">
                AI-powered medical evidence platform for secure, compliant medico-legal case management —
                informed by practising UK clinicians.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-medical-800 border border-medical-700 rounded-full text-xs text-medical-300">
                  <ShieldCheck className="h-3 w-3" />
                  <span>NHS DSPT</span>
                </span>
                <span className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-medical-800 border border-medical-700 rounded-full text-xs text-medical-300">
                  <MapPin className="h-3 w-3" />
                  <span>UK Data Residency</span>
                </span>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Product</h3>
              <ul className="space-y-3 text-medical-300">
                <li><Link to="/security" onClick={scroll} className="hover:text-white transition-colors">Security</Link></li>
                <li><Link to="/signup" onClick={scroll} className="hover:text-white transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-3 text-medical-300">
                <li><Link to="/team" onClick={scroll} className="hover:text-white transition-colors">Team</Link></li>
                <li><Link to="/jobs" onClick={scroll} className="hover:text-white transition-colors">Careers</Link></li>
                <li><Link to="/demo" onClick={scroll} className="hover:text-white transition-colors">Watch the Demo</Link></li>
                <li><Link to="/contact" onClick={scroll} className="hover:text-white transition-colors">Contact</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-medical-800 pt-8 space-y-2">
            <p className="text-sm text-medical-400 text-center">
              &copy; {new Date().getFullYear()} ClinLexis&trade;. All rights reserved.
            </p>
            <p className="text-xs text-medical-500 text-center">
              ClinLexis Ltd &middot; Company No. 17081483 &middot; Registered in England and Wales &middot; 238a Telegraph Road, Heswall, Wirral, CH60 0AL
            </p>
            <p className="text-xs text-medical-500 text-center">
              <Link to="/terms" onClick={scroll} className="hover:text-white transition-colors">Terms of Service</Link>
              {' '}&middot;{' '}
              <Link to="/privacy" onClick={scroll} className="hover:text-white transition-colors">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
